import { useCallback, useEffect } from 'react';

import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import CodeBlock from '@/components/atoms/CodeBlock/CodeBlock';
import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion, selectWebchatEmbedSnippet } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, WebWidgetIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

type Form = { widget_position: string; widget_alignment: string };

const positionOptions = [
  { value: 'livechat', label_key: 'integrations.web_position.livechat' },
  { value: 'embed', label_key: 'integrations.web_position.embed' },
  { value: 'popup', label_key: 'integrations.web_position.popup' },
];

const alignmentOptions = [
  { value: 'right', label_key: 'integrations.web_position.alignment.right' },
  { value: 'left', label_key: 'integrations.web_position.alignment.left' },
];

const toAlignment = (saved?: string) => (saved?.endsWith('left') ? 'left' : 'right');

const toSavedAlignment = (alignment: string, saved?: string) =>
  saved?.startsWith('top-') ? `top-${alignment}` : alignment;

const WebchatPosition = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, WebWidgetIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const codeSnippet = useSelector((state: RootState) =>
    selectWebchatEmbedSnippet(state, integration?.integration_id)
  );
  const dispatch = useDispatch();
  const { updateIntegration, updateStatus } = useIntegrations<WebWidgetIntegration>(
    integration?.desk_id,
    integration?.integration_id
  );

  const formMethods = useForm<Form>({ mode: 'onSubmit' });
  const {
    control,
    reset,
    formState: { isDirty },
    setValue,
    trigger,
    handleSubmit,
  } = formMethods;

  useEffect(() => {
    reset({
      widget_position: integration?.config?.widget_position || 'livechat',
      widget_alignment: toAlignment(integration?.config?.widget_alignment),
    });
  }, [integration?.config?.widget_position, integration?.config?.widget_alignment, reset]);

  const [widgetPosition, widgetAlignment] = useWatch({
    control,
    name: ['widget_position', 'widget_alignment'],
  });
  const isEmbed = widgetPosition === 'embed';

  const onSubmit = useCallback(() => {
    const { widget_position, widget_alignment } = formMethods.getValues();
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration?.config,
          widget_position,
          widget_alignment: toSavedAlignment(
            widget_alignment,
            integration?.config?.widget_alignment
          ),
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, formMethods, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(onSubmit));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [dispatch, expanded, isDirty, reset, type]);

  const Snippet = () => <span>{codeSnippet}</span>;

  return (
    <>
      <Accordion
        title={t('integrations.web_position.title')}
        subtitle={t('integrations.web_position.subtitle')}
        footerText={t('integrations.web_position.footer')}
        onSubmit={handleSubmit(onSubmit)}
        disabled={!isDirty}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(onSubmit), trigger)}
      >
        <div className="space-y-2">
          <Label htmlFor="widget_position" className="text-foreground">
            {t('integrations.web_position.select')}
          </Label>
          <Select
            value={widgetPosition}
            onValueChange={(value) => setValue('widget_position', value, { shouldDirty: true })}
            disabled={!canWrite}
          >
            <SelectTrigger id="widget_position" className="w-[250px] text-foreground">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {positionOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {t(option.label_key)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {!isEmbed && (
          <div className="space-y-2 pt-6">
            <Label htmlFor="widget_alignment" className="text-foreground">
              {t('integrations.web_position.alignment.select')}
            </Label>
            <Select
              value={widgetAlignment}
              onValueChange={(value) => setValue('widget_alignment', value, { shouldDirty: true })}
              disabled={!canWrite}
            >
              <SelectTrigger id="widget_alignment" className="w-[250px] text-foreground">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {alignmentOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {t(option.label_key)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {isEmbed && (
          <div className="pt-6">
            <TitleSubtitle
              title={t('integrations.web_position.title2')}
              subtitle={t('integrations.web_position.subtitle2')}
            />
            <div>
              <div className="flex items-center">
                <CodeBlock>
                  <Snippet />
                </CodeBlock>
                {codeSnippet && <CopyButton className="pl-6" data={codeSnippet} />}
              </div>
            </div>
          </div>
        )}
      </Accordion>
    </>
  );
};

export default WebchatPosition;
